import { ReactNode } from 'react';

import { Variant } from '@mui/material/styles/createTypography';
import { useTheme } from '@mui/material/styles';

import { Box, BoxColors, SpacingValues } from '../Box';
import { Typography } from '../Typography';
import { Icon, IconColor, IconNames } from '../Icon';

type CalloutVariant = 'error' | 'warning' | 'primary' | 'paper';
type Size = 'small' | 'medium' | 'large' | 'none';

/**
 * Свойства компонента {@link Callout}
 */
export interface CalloutProps {
  /**
   * Нужно ли отображать границу для каллоута.
   *
   * @defaultValue `false`
   */
  bordered?: boolean;

  /**
   * Содержимое, которое будет отображено внутри каллоута.
   */
  children: ReactNode | ReactNode[];

  /**
   * Нужно ли отображать иконку в каллоуте.
   *
   * @defaultValue `true`
   */
  showIcon?: boolean;

  /**
   * Вариант каллоута.
   *
   * @defaultValue `'primary'`
   */
  variant?: CalloutVariant;

  /**
   * Нужно ли отображать фоновый цвет для каллоута.
   *
   * @defaultValue `true`
   */
  withBackground?: boolean;

  /**
   * Нужно ли разворачивать каллоут для отображения дополнительного содержимого.
   *
   * @defaultValue `false`
   */
  expanded?: boolean;

  /**
   * Заголовок, который будет отображен в каллоуте.
   */
  title?: string;

  /**
   * Размер каллоута.
   *
   * @defaultValue `'medium'`
   */
  size?: Size;
}

/**
 * Компонент для отображения каллоута с опциональной границей, фоновым цветом и иконкой.
 * Поддерживает различные варианты и размеры, а также может быть развернут для отображения дополнительного содержимого.
 *
 * @public
 * @param bordered - Нужно ли отображать границу для каллоута.
 * @param children - Содержимое, которое будет отображено внутри каллоута.
 * @param showIcon - Нужно ли отображать иконку в каллоуте.
 * @param variant - Вариант каллоута.
 * @param withBackground - Нужно ли отображать фоновый цвет для каллоута.
 * @param expanded - Нужно ли разворачивать каллоут для отображения дополнительного содержимого.
 * @param title - Заголовок, который будет отображен в каллоуте.
 * @param size - Размер каллоута.
 * @returns Компонент Callout.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link CalloutProps}
 */
export const Callout = ({
  bordered = false,
  children,
  title,
  showIcon = true,
  withBackground = true,
  variant = 'primary',
  expanded = false,
  size = 'medium',
}: CalloutProps) => {
  const theme = useTheme();

  const iconName: Record<CalloutVariant, IconNames> = {
    error: 'error_circle_outline',
    warning: 'error_circle_outline',
    primary: 'info_circle_outline',
    paper: 'info_circle_outline',
  };

  const borderColor: Record<CalloutVariant, BoxColors> = {
    error: 'accent.text.error',
    warning: 'accent.text.warning',
    primary: 'stroke.primary',
    paper: 'stroke.primary',
  };

  const bgColor: Record<CalloutVariant, BoxColors> = {
    error: 'accent.bg.error',
    warning: 'accent.bg.warning',
    primary: 'bg.greyscale.primary',
    paper: 'bg.light.primary',
  };

  const color: Record<CalloutVariant, BoxColors> = {
    error: 'accent.text.error',
    warning: 'accent.text.warning',
    primary: 'text.light.primary',
    paper: 'text.light.primary',
  };

  const iconColor: Record<CalloutVariant, IconColor> = {
    error: 'accent.text.error',
    warning: 'accent.text.warning',
    primary: 'original.brand.primary',
    paper: 'original.brand.primary',
  };

  const expandedPaddingBySize: Record<Size, SpacingValues> = {
    small: '16',
    medium: '24',
    large: '32',
    none: '0',
  };

  const paddingXBySize: Record<Size, SpacingValues> = {
    small: '12',
    medium: '16',
    large: '24',
    none: '0',
  };
  const paddingYBySize: Record<Size, SpacingValues> = {
    small: '4',
    medium: '8',
    large: '16',
    none: '0',
  };

  const typographyTitleVariant: Record<Size, Variant> = {
    small: 'subtitle2',
    medium: 'subtitle1',
    large: 'h6',
    none: 'body2',
  };

  return (
    <Box
      bgcolor={withBackground ? bgColor[variant] : undefined}
      radius={size === 'large' ? 'l' : 'm'}
      border={bordered ? '1px solid' : 'none'}
      borderColor={withBackground ? borderColor[variant] : borderColor.primary}
      color={withBackground ? color[variant] : color.primary}
      className="aqa_callout"
      testId="aqa_callout"
      alignItems="flex-start"
      px={expanded ? expandedPaddingBySize[size] : paddingXBySize[size]}
      py={expanded ? expandedPaddingBySize[size] : paddingYBySize[size]}
    >
      {expanded ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
        >
          <Box display="flex" alignItems="center" mb="8">
            {showIcon && (
              <Box
                mr={size === 'small' ? '8' : '12'}
                display="flex"
                alignItems="center"
                alignSelf="flex-start"
                color={color[variant]}
              >
                <Icon
                  name={iconName[variant]}
                  size="20"
                  color={iconColor[variant]}
                  data-testid="icon"
                />
              </Box>
            )}
            {title && (
              <Typography variant={typographyTitleVariant[size]}>
                {title}
              </Typography>
            )}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            fontSize={
              size === 'large'
                ? theme.tokens.typography.variant.body2.fontSize
                : theme.tokens.typography.variant.body3.fontSize
            }
          >
            {children}
          </Box>
        </Box>
      ) : (
        <Box display="flex" alignItems="flex-start">
          {showIcon && (
            <Box
              mr={size === 'small' ? '8' : '12'}
              color={color[variant]}
              display="flex"
              alignItems="center"
            >
              <Icon
                name={iconName[variant]}
                size="20"
                color={iconColor[variant]}
              />
            </Box>
          )}
          {children}
        </Box>
      )}
    </Box>
  );
};
